/**
 * Spreadsheet grid store (Zustand, not persisted)
 *
 * Lets the chat change card steer the grid of the spreadsheet it changed,
 * keyed by artifact id. An undo lands server side while an open Univer grid
 * keeps its stale copy (it shows a "Collaboration Conflict" notice), so the
 * card suspends the grid (unmounts Univer) around the request and resumes it
 * after (P3-D1). A suspended grid is not rendered at all, so EVERY resume that
 * ends the last suspension mounts a fresh Univer, whatever the outcome, and
 * bumps `generation` (part of the grid's React key). The page holds Univer's
 * autofocus for a mount whose generation a card moved, so every one of those
 * fresh mounts leaves focus on the card's status.
 *
 * An undo the agent runs from chat (`spreadsheet_undo`) is not suspended
 * around: it has already happened when the frame arrives, so `remount` only
 * bumps `generation`, the same fresh mount (and the same autofocus hold).
 *
 * `suspensions` counts undos in flight, not a flag: two cards for one sheet
 * can overlap, and the first to finish must not bring the grid back under the
 * second's request. The grid stays unmounted while it is above zero.
 *
 * `historyRequested` is a version-history request not yet served, for a sheet
 * whose page is showing: the page opens history once when it is set and a
 * grid is ready, then consumes it, and drops it unserved when it stops showing
 * the sheet. It is only ever set while the page shows the sheet: a card
 * beside the open sheet sets it in place, and a card elsewhere navigates to
 * the sheet carrying `OpenHistoryNavState`, which the page turns into this
 * request on arrival. A navigation an unsaved-changes prompt blocks never
 * arrives, so it leaves nothing behind, and one that arrives late (after Save
 * or Discard) is still served: no clock decides.
 */

import { create } from 'zustand';

/** Router state a card puts on its navigation to a sheet to open its history there. */
export interface OpenHistoryNavState {
  readonly openHistory: true;
}

export interface SpreadsheetGridEntry {
  readonly suspensions: number;
  readonly generation: number;
  /** A history request is pending for this sheet's page. */
  readonly historyRequested: boolean;
}

interface SpreadsheetGridState {
  grids: Readonly<Record<string, SpreadsheetGridEntry>>;
  suspend: (artifactId: string) => void;
  resume: (artifactId: string) => void;
  /**
   * A fresh mount now, with no suspension around it: the agent undid a change
   * from chat, so the revert already happened server side while the grid was
   * live, and only a new Univer drops its stale copy and conflict notice.
   */
  remount: (artifactId: string) => void;
  requestHistory: (artifactId: string) => void;
  /** Marks the pending history request for this artifact as served (or dropped). */
  consumeHistoryRequest: (artifactId: string) => void;
}

export const IDLE_GRID: SpreadsheetGridEntry = {
  suspensions: 0,
  generation: 0,
  historyRequested: false,
};

function update(
  grids: SpreadsheetGridState['grids'],
  artifactId: string,
  change: (entry: SpreadsheetGridEntry) => Partial<SpreadsheetGridEntry>,
) {
  const entry = grids[artifactId] ?? IDLE_GRID;
  return { grids: { ...grids, [artifactId]: { ...entry, ...change(entry) } } };
}

export const useSpreadsheetGridStore = create<SpreadsheetGridState>((set) => ({
  grids: {},
  suspend: (artifactId) =>
    set((s) => update(s.grids, artifactId, (e) => ({ suspensions: e.suspensions + 1 }))),
  // Only the resume that ends the LAST suspension brings the grid back, as a
  // fresh mount; a stray resume with nothing suspended changes nothing.
  resume: (artifactId) =>
    set((s) =>
      update(s.grids, artifactId, (e) => ({
        suspensions: Math.max(0, e.suspensions - 1),
        generation: e.suspensions === 1 ? e.generation + 1 : e.generation,
      })),
    ),
  remount: (artifactId) =>
    set((s) => update(s.grids, artifactId, (e) => ({ generation: e.generation + 1 }))),
  requestHistory: (artifactId) =>
    set((s) => update(s.grids, artifactId, () => ({ historyRequested: true }))),
  consumeHistoryRequest: (artifactId) =>
    set((s) => update(s.grids, artifactId, () => ({ historyRequested: false }))),
}));

/** One artifact's grid state; the idle entry until something touches it. */
export function useSpreadsheetGrid(artifactId: string | undefined): SpreadsheetGridEntry {
  return useSpreadsheetGridStore(
    (s) => (artifactId ? s.grids[artifactId] : undefined) ?? IDLE_GRID,
  );
}
